'use client'

import { ReactNode, useState } from 'react'

import { getWordEnding } from '@/shared/lib/utils/string/get-word-ending'
import { Button, Text } from '@/shared/ui'
import { Drawer } from '@/shared/ui/drawer'
import { Icon } from '@/shared/ui/icon'

import { CatalogFilterController } from '../model'

import { FilterIcon } from './filter-icon'

import cls from './drawer-filter.module.scss'

type FilterHandlers = { onFilterChange: (filterUrl: string) => void }
type AppliedFiltersHandlers = { onChange: (filterUrl: string) => void; className: string }

interface CatalogDrawerFilterProps {
	/** Количество товаров для кнопки «Показать N товаров». */
	productCount: number
	/** Контроллер фильтра раздела (результат use*Filter). */
	controller: CatalogFilterController
	/** Держать drawer открытым после клика фильтра (discount / markdown / shop). */
	reopenOnChange?: boolean
	/** Доп. класс для Drawer (напр. min-width у markdown). */
	className?: string
	/** Список категорий над фильтром (опционально). */
	categoriesSlot?: ReactNode
	/** Рендер фильтра раздела. Обработчик — для секций, чей Filter принимает onFilterChange. */
	renderFilter: (handlers: FilterHandlers) => ReactNode
	/** Рендер панели применённых фильтров раздела. */
	renderAppliedFilters: (handlers: AppliedFiltersHandlers) => ReactNode
}

/**
 * Общая оболочка мобильного фильтра каталога (кнопка + Drawer + панели + «Показать N товаров»).
 * Секционные Filter / AppliedFilters / CategoriesList передаются слотами, т.к. завязаны на свой хук.
 */
export const CatalogDrawerFilter = (props: CatalogDrawerFilterProps) => {
	const { productCount, controller, reopenOnChange, className, categoriesSlot, renderFilter, renderAppliedFilters } =
		props
	const { appliedFiltersCount, isLoading, onFilterChange } = controller

	const [open, setOpen] = useState(false)

	// Обёртка: применяет фильтр и (для discount/markdown) удерживает drawer открытым.
	const handleFilterChange = (filterValue: string) => {
		onFilterChange(filterValue)

		if (reopenOnChange) {
			setTimeout(() => {
				setOpen(true)
			}, 100)
		}
	}

	const topPanel = (
		<div className={cls.drawerTopPanel}>
			<div />
			<Text className={cls.drawerPanelText}>Фильтры</Text>
			<button className={cls.closeButton} onClick={() => setOpen(false)}>
				<Icon color="#1F22273D" size={24} name="Close" />
			</button>
		</div>
	)

	const bottomPanel = (
		<div className={cls.mobileBottomPanel}>
			{renderAppliedFilters({ onChange: handleFilterChange, className: cls.applyFiltersPanelWrap })}

			<Button fullWidth onClick={() => setOpen(false)} isLoading={isLoading} disabled={isLoading}>
				Показать {productCount} {getWordEnding(`товар`, productCount)}
			</Button>
		</div>
	)

	return (
		<>
			<button className={cls.openFilterButton} onClick={() => setOpen(!open)}>
				{!!appliedFiltersCount && <div className={cls.count}>{appliedFiltersCount}</div>}
				<Text>Фильтры</Text>
				<FilterIcon />
			</button>
			<Drawer
				isOpen={open}
				onClose={() => setOpen(false)}
				topPanel={topPanel}
				bottomPanel={bottomPanel}
				className={className}>
				{categoriesSlot}
				{renderFilter({ onFilterChange: handleFilterChange })}
			</Drawer>
		</>
	)
}
